/**
 * Argus Pilot Hotel Onboarding — protocol-admin ops console.
 *
 * Single-screen workflow: enter hotel_id + admin wallet → click Onboard
 * → live timeline shows mint → registerSigner → grantHotelAdmin with
 * tx hashes deep-linked to BaseScan. Idempotent resume + rotation +
 * admin revocation handled here too.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Plus, RotateCcw, Trash2, ExternalLink,
  CheckCircle2, XCircle, Clock, Layers, Shield, Wallet, ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { API } from '@/lib/backendUrl';

const STATUS_COLORS = {
  active: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  pending: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  failed: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
};
const STEP_ICON = {
  ok: <CheckCircle2 className="w-4 h-4 text-emerald-400" />,
  skipped: <CheckCircle2 className="w-4 h-4 text-zinc-500" />,
  failed: <XCircle className="w-4 h-4 text-rose-400" />,
};

function shortHash(h, n = 10) {
  if (!h) return '—';
  return `${h.slice(0, n)}…${h.slice(-6)}`;
}
function explorerTx(hash, chainId) {
  if (!hash) return null;
  if (chainId === 84532) return `https://sepolia.basescan.org/tx/${hash}`;
  if (chainId === 8453) return `https://basescan.org/tx/${hash}`;
  return null;
}
function explorerAddr(addr, chainId) {
  if (!addr) return null;
  if (chainId === 84532) return `https://sepolia.basescan.org/address/${addr}`;
  if (chainId === 8453) return `https://basescan.org/address/${addr}`;
  return null;
}

export default function ArgusOnboardingPage() {
  const nav = useNavigate();
  const [health, setHealth] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  // Form state for new onboarding
  const [hotelId, setHotelId] = useState('');
  const [hotelAdminWallet, setHotelAdminWallet] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [hRes, lRes] = await Promise.all([
        fetch(`${API}/api/argus/onboarding/health`),
        fetch(`${API}/api/argus/onboarding/list/all`),
      ]);
      setHealth(await hRes.json());
      const j = await lRes.json();
      setItems(j.onboardings || []);
    } catch {
      setItems([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!hotelId.trim() || !hotelAdminWallet.trim()) {
      setActionMsg({ type: 'err', text: 'hotel_id and hotel_admin_wallet are required' });
      return;
    }
    if (!/^0x[a-fA-F0-9]{40}$/.test(hotelAdminWallet.trim())) {
      setActionMsg({ type: 'err', text: 'admin wallet must be a 0x-prefixed 42-char address' });
      return;
    }
    setSubmitting(true);
    setActionMsg(null);
    try {
      const r = await fetch(`${API}/api/argus/onboarding/hotel/${encodeURIComponent(hotelId.trim())}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hotel_admin_wallet: hotelAdminWallet.trim(),
          created_by: 'admin-console',
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'onboarding failed');
      setActionMsg({
        type: j.status === 'active' ? 'ok' : 'err',
        text: j.status === 'active' ? `Onboarded ${hotelId} ✓` : `${hotelId}: ${j.current_step} ${j.status}`,
      });
      setHotelId('');
      setHotelAdminWallet('');
      await load();
    } catch (e) {
      setActionMsg({ type: 'err', text: e.message });
    }
    setSubmitting(false);
  };

  const resume = async (hotelId) => {
    setActionMsg(null);
    try {
      const r = await fetch(`${API}/api/argus/onboarding/hotel/${encodeURIComponent(hotelId)}/resume`, { method: 'POST' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'resume failed');
      setActionMsg({ type: 'ok', text: `Resumed ${hotelId} → ${j.status}` });
      await load();
    } catch (e) {
      setActionMsg({ type: 'err', text: e.message });
    }
  };

  const rotate = async (hotelId) => {
    if (!window.confirm(`Rotate signer for ${hotelId}? This will mint a fresh key, register it on-chain, and revoke the previous one.`)) return;
    setActionMsg(null);
    try {
      const r = await fetch(`${API}/api/argus/onboarding/hotel/${encodeURIComponent(hotelId)}/rotate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ created_by: 'admin-console', revoke_old: true }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'rotate failed');
      setActionMsg({ type: 'ok', text: `Rotated ${hotelId} → new ${shortHash(j.new_signer_address)}` });
      await load();
    } catch (e) {
      setActionMsg({ type: 'err', text: e.message });
    }
  };

  const revokeAdmin = async (hotelId, currentAdmin) => {
    const adminAddr = window.prompt('Wallet address to revoke from HOTEL_ADMIN_ROLE:', currentAdmin || '');
    if (!adminAddr) return;
    if (!/^0x[a-fA-F0-9]{40}$/.test(adminAddr.trim())) {
      setActionMsg({ type: 'err', text: 'invalid wallet address' });
      return;
    }
    setActionMsg(null);
    try {
      const r = await fetch(`${API}/api/argus/onboarding/hotel/${encodeURIComponent(hotelId)}/revoke-admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_addr: adminAddr.trim(), revoked_by: 'admin-console' }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'revoke failed');
      setActionMsg({ type: 'ok', text: `Revoked admin ${shortHash(adminAddr)} for ${hotelId}` });
      await load();
    } catch (e) {
      setActionMsg({ type: 'err', text: e.message });
    }
  };

  const chainId = health?.chain_id || 84532;

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-zinc-100">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => nav('/admin')} data-testid="onboarding-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin
            </Button>
            <div>
              <h1 className="text-2xl font-semibold flex items-center gap-2">
                <Layers className="w-6 h-6 text-amber-400" />
                Pilot Hotel Onboarding
              </h1>
              <p className="text-xs text-zinc-400">
                Mint key → Register on-chain → Grant HOTEL_ADMIN_ROLE · idempotent · resumable
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={load} data-testid="onboarding-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Health card */}
        <Card className="bg-zinc-900/60 border-zinc-800 mb-5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-amber-400" />
                <div>
                  <div className="text-sm font-medium">
                    {health?.configured ? 'Registry connected' : 'Registry NOT connected'}
                  </div>
                  <div className="text-[11px] text-zinc-500 font-mono">
                    {health?.registry_address ? (
                      <a
                        href={explorerAddr(health.registry_address, chainId)}
                        target="_blank" rel="noreferrer"
                        className="hover:text-amber-300"
                        data-testid="onboarding-registry-link"
                      >
                        {shortHash(health.registry_address, 12)} <ExternalLink className="inline w-3 h-3" />
                      </a>
                    ) : '—'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <div>
                  <div className="text-zinc-500">chain</div>
                  <div className="text-zinc-200">{chainId}</div>
                </div>
                <div>
                  <div className="text-zinc-500">block</div>
                  <div className="text-zinc-200">{health?.block_number ?? '—'}</div>
                </div>
                <div>
                  <div className="text-zinc-500">deployer balance</div>
                  <div className={`${(health?.deployer_balance_eth ?? 0) > 0.01 ? 'text-emerald-300' : 'text-rose-300'}`}>
                    {health?.deployer_balance_eth ? health.deployer_balance_eth.toFixed(4) : '—'} ETH
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Onboard form */}
        <Card className="bg-zinc-900/60 border-zinc-800 mb-5">
          <CardContent className="p-4">
            <div className="text-sm font-medium mb-2 flex items-center gap-2">
              <Plus className="w-4 h-4 text-amber-400" /> Onboard a new pilot hotel
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input
                type="text"
                value={hotelId}
                onChange={(e) => setHotelId(e.target.value)}
                placeholder="hotel_id (e.g. hotel_pilot_zurich)"
                className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"
                data-testid="onboarding-hotel-id-input"
              />
              <input
                type="text"
                value={hotelAdminWallet}
                onChange={(e) => setHotelAdminWallet(e.target.value)}
                placeholder="hotel admin wallet (0x...)"
                className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm font-mono"
                data-testid="onboarding-admin-wallet-input"
              />
              <Button
                onClick={submit}
                disabled={submitting || !health?.configured}
                className="bg-amber-600 hover:bg-amber-500"
                data-testid="onboarding-submit-btn"
              >
                {submitting ? 'Onboarding (3 txs)…' : 'Onboard hotel'}
              </Button>
            </div>
            <div className="text-[11px] text-zinc-500 mt-2">
              Runs three on-chain steps: mint per-hotel signer (off-chain) → registerSigner → grantHotelAdmin.
              Average completion ~1.5s. Failed runs are resumable.
            </div>
          </CardContent>
        </Card>

        {actionMsg && (
          <div
            className={`mb-3 px-3 py-2 rounded text-sm ${
              actionMsg.type === 'ok'
                ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
                : 'bg-rose-500/10 text-rose-300 border border-rose-500/30'
            }`}
            data-testid="onboarding-action-msg"
          >
            {actionMsg.text}
          </div>
        )}

        {/* Onboardings list */}
        {loading ? (
          <div className="text-zinc-500 py-12 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <Card className="bg-zinc-900/40 border-zinc-800">
            <CardContent className="py-10 text-center text-zinc-500">
              No onboardings yet. Onboard the first pilot above.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {items.map((row) => (
              <OnboardingRow
                key={row.id}
                row={row}
                chainId={chainId}
                open={openId === row.id}
                onToggle={() => setOpenId(openId === row.id ? null : row.id)}
                onResume={() => resume(row.hotel_id)}
                onRotate={() => rotate(row.hotel_id)}
                onRevokeAdmin={() => revokeAdmin(row.hotel_id, row.hotel_admin_wallet)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OnboardingRow({ row, chainId, open, onToggle, onResume, onRotate, onRevokeAdmin }) {
  const isComplete = row.status === 'active';
  const isFailed = row.status === 'failed';
  const isRotation = row.kind === 'rotation';
  const isRevocation = row.kind === 'admin_revocation';

  return (
    <Card className="bg-zinc-900/60 border-zinc-800" data-testid={`onboarding-row-${row.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <button
            onClick={onToggle}
            className="text-zinc-500 hover:text-zinc-200 mt-1"
            data-testid={`onboarding-toggle-${row.id}-btn`}
          >
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className="font-mono text-amber-300">{row.hotel_id}</span>
              {isRotation ? (
                <Badge className="bg-blue-500/15 text-blue-300 border-blue-500/30">rotation</Badge>
              ) : isRevocation ? (
                <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/30">admin revoke</Badge>
              ) : (
                <Badge className={STATUS_COLORS[row.status] || ''}>{row.status}</Badge>
              )}
              {row.current_step && row.current_step !== 'complete' && (
                <Badge className="bg-zinc-800 text-zinc-300 border-zinc-700">at: {row.current_step}</Badge>
              )}
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-3 flex-wrap">
              <span>{new Date(row.created_at).toLocaleString()}</span>
              {row.hotel_admin_wallet && (
                <span><Wallet className="inline w-3 h-3 mr-1" />admin <span className="font-mono">{shortHash(row.hotel_admin_wallet)}</span></span>
              )}
              {row.off_chain_signer_address && (
                <span>signer <a href={explorerAddr(row.off_chain_signer_address, chainId)} target="_blank" rel="noreferrer" className="font-mono hover:text-amber-300">
                  {shortHash(row.off_chain_signer_address)}</a></span>
              )}
              {row.new_signer_address && (
                <span>new signer <a href={explorerAddr(row.new_signer_address, chainId)} target="_blank" rel="noreferrer" className="font-mono hover:text-amber-300">
                  {shortHash(row.new_signer_address)}</a></span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {isFailed && (
              <Button size="sm" onClick={onResume} className="bg-amber-600 hover:bg-amber-500" data-testid={`onboarding-resume-${row.id}-btn`}>
                <RefreshCw className="w-4 h-4 mr-1" /> Resume
              </Button>
            )}
            {isComplete && !isRotation && !isRevocation && (
              <>
                <Button size="sm" variant="outline" onClick={onRotate} className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10" data-testid={`onboarding-rotate-${row.id}-btn`}>
                  <RotateCcw className="w-4 h-4 mr-1" /> Rotate
                </Button>
                <Button size="sm" variant="outline" onClick={onRevokeAdmin} className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10" data-testid={`onboarding-revoke-${row.id}-btn`}>
                  <Trash2 className="w-4 h-4 mr-1" /> Revoke admin
                </Button>
              </>
            )}
          </div>
        </div>

        {open && (
          <div className="mt-3 pt-3 border-t border-zinc-800/80 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div>
              <div className="text-zinc-500 mb-1">Timeline</div>
              <div className="bg-black/40 p-2 rounded space-y-1">
                {(row.timeline || []).length === 0 && (
                  <div className="text-zinc-600 italic">no events</div>
                )}
                {(row.timeline || []).map((t, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="mt-0.5">{STEP_ICON[t.status] || <Clock className="w-4 h-4 text-zinc-500" />}</div>
                    <div className="flex-1">
                      <div className="text-zinc-200">{t.step} <span className="text-zinc-500">· {t.status}</span></div>
                      {t.tx && (
                        <a href={explorerTx(t.tx, chainId)} target="_blank" rel="noreferrer" className="font-mono text-[10px] text-amber-300 hover:text-amber-200">
                          {shortHash(t.tx, 14)} <ExternalLink className="inline w-2.5 h-2.5" />
                        </a>
                      )}
                      {t.error && <div className="text-rose-300 text-[11px]">{t.error}</div>}
                      {t.note && <div className="text-zinc-500 text-[11px] italic">{t.note}</div>}
                      <div className="text-zinc-600 text-[10px]">{new Date(t.ts).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-zinc-500 mb-1">On-chain receipts</div>
              <div className="bg-black/40 p-2 rounded space-y-1.5">
                {row.onchain && (
                  <>
                    <div>
                      <span className="text-zinc-500">registerSigner</span>{' '}
                      {row.onchain.register_signer_tx ? (
                        <a href={explorerTx(row.onchain.register_signer_tx, chainId)} target="_blank" rel="noreferrer" className="font-mono text-amber-300 hover:text-amber-200">
                          {shortHash(row.onchain.register_signer_tx)} <ExternalLink className="inline w-2.5 h-2.5" />
                        </a>
                      ) : <span className="text-zinc-600">—</span>}
                      {row.onchain.register_signer_block && <span className="text-zinc-500"> @ block {row.onchain.register_signer_block}</span>}
                    </div>
                    <div>
                      <span className="text-zinc-500">grantHotelAdmin</span>{' '}
                      {row.onchain.grant_admin_tx ? (
                        <a href={explorerTx(row.onchain.grant_admin_tx, chainId)} target="_blank" rel="noreferrer" className="font-mono text-amber-300 hover:text-amber-200">
                          {shortHash(row.onchain.grant_admin_tx)} <ExternalLink className="inline w-2.5 h-2.5" />
                        </a>
                      ) : <span className="text-zinc-600">—</span>}
                      {row.onchain.grant_admin_block && <span className="text-zinc-500"> @ block {row.onchain.grant_admin_block}</span>}
                    </div>
                  </>
                )}
                {row.register_new_tx && (
                  <div>
                    <span className="text-zinc-500">registerSigner (new)</span>{' '}
                    <a href={explorerTx(row.register_new_tx, chainId)} target="_blank" rel="noreferrer" className="font-mono text-amber-300">
                      {shortHash(row.register_new_tx)} <ExternalLink className="inline w-2.5 h-2.5" />
                    </a>
                  </div>
                )}
                {row.revoke_old_tx && (
                  <div>
                    <span className="text-zinc-500">revokeSigner (old)</span>{' '}
                    <a href={explorerTx(row.revoke_old_tx, chainId)} target="_blank" rel="noreferrer" className="font-mono text-rose-300">
                      {shortHash(row.revoke_old_tx)} <ExternalLink className="inline w-2.5 h-2.5" />
                    </a>
                  </div>
                )}
                {row.tx_hash && row.kind === 'admin_revocation' && (
                  <div>
                    <span className="text-zinc-500">revokeHotelAdmin</span>{' '}
                    <a href={explorerTx(row.tx_hash, chainId)} target="_blank" rel="noreferrer" className="font-mono text-rose-300">
                      {shortHash(row.tx_hash)} <ExternalLink className="inline w-2.5 h-2.5" />
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
